// src/pages/Home.js
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DentalLogo from "../assets/images/dentallabor_schreiber.png";
import LoginImage from "../assets/images/Login.png";
import axiosInstance from "../services/axiosInstance";
import { UserContext } from "../contexts/UserContext";
import { ERROR_MESSAGES_LOGIN } from "../enums/Constants";
import { CircularProgress } from "@mui/material";

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { userData, saveUserData, clearUserData, saveToken } =
    useContext(UserContext);
  const [formData, setFormData] = useState({
    id: "",
    pin: "",
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const idInputRef = React.useRef(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    let newValue = value;

    // Convert to uppercase if the field is "id"
    if (id === "id") {
      newValue = value.toUpperCase();
      if (newValue.length > 3) return; // Limit to 3 characters
    }

    // Restrict input length and non-numeric values for "pin"
    if (id === "pin" && (!/^\d*$/.test(value) || value.length > 4)) return;

    setFormData({ ...formData, [id]: newValue }); // Use newValue instead of value

    // Reset error for the field
    setErrors({ ...errors, [id]: "" });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Validate ID (3-letter, all-caps)
    if (!/^[A-Za-z]{3}$/.test(formData.id)) {
      isValid = false;
      newErrors.id = ERROR_MESSAGES_LOGIN.UID;
    }

    //Validate Pin (4 digits)
    if (!/^\d{4}$/.test(formData.pin)) {
      isValid = false;
      newErrors.pin = ERROR_MESSAGES_LOGIN.PIN;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      //console.log("Form submitted:", formData);
      setIsLoading(true);
      postUserData();
    }
  };

  const postUserData = async () => {
    try {
      const response = await axiosInstance.post("/api/auth/login", {
        Username: formData.id,
        Password: formData.pin,
      });

      saveUserData(response);
      saveToken(response?.token, response?.refreshToken);
      //console.log("User data:", response);
      if (response.isFirstTimeLogin) {
        navigate(`/reset-password?token=${response.resetPasswordToken}`);
      } else if (response.token && !response.isFirstTimeLogin) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      if (error.response && error.response?.data) {
        const serverErrors = error?.response?.data || error.response || {};
        setFormError(serverErrors || ERROR_MESSAGES_LOGIN.UNEXPECTED);
      } else {
        setFormError(ERROR_MESSAGES_LOGIN.UNEXPECTED);
      }
    } finally {
      setIsLoading(false); // Hide loader when response comes
    }
  };

  useEffect(() => {
    document.body.classList.add("login-page");
    idInputRef.current?.focus();

    // Cleanup: Remove the class when component unmounts
    return () => {
      document.body.classList.remove("login-page");
    };
  }, []);

  useEffect(() => {
    if (userData && !userData.isFirstTimeLogin) navigate("/dashboard");
  }, [userData]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission on pressing Enter
      handleSubmit(); // Trigger the form submission logic
    }
  };

  const navigate = useNavigate();
  // const DashboardPage = () => {
  //   navigate("/dashboard");
  // };
  return (
    <div className="flex items-center h-screen">
      <div className="lg:w-1/2 w-full lg:block hidden">
        <img
          src={LoginImage}
          alt="Login"
          className=" w-full object-cover h-screen"
        />
      </div>
      <div
        className="relative self-center lg:w-1/2 w-full h-full px-5 flex justify-center items-center
        before:content-[''] before:bottom-0 before:right-0 before:w-full before:h-full before:bg-auto before:bg-right-bottom before:bg-no-repeat before:absolute before:bg-[url('./assets/images/schreiber-right.svg')]  before:lg:bg-transparent before:bg-[#0000004a]
        after:content-[''] after:bottom-0 after:left-0 after:w-[150px] after:h-[166px] after:bg-auto after:bg-left-bottom after:bg-no-repeat after:absolute after:bg-[url('./assets/images/schreiber-left.svg')] 
        "
      >
        <div className="relative 2xl:w-5/12 xl:w-1/2 lg:w-3/4 w-5/6 bg-white rounded-3xl lg:p-0 p-8">
          <img
            src={DentalLogo}
            className="mx-auto"
            alt="Dentallabor Schreiber"
          />
          <h1 className="pt-7 lg:pt-10 2xl:pt-20 text-center text-5xl font-bold font-roboto text-gray-600 mb-8">
            Willkommen
          </h1>
          {formError && <p className="text-red text-sm">{formError}</p>}
          <form className="login_page">
            <div className="mb-5 font-inter">
              <label
                className="block text-gray-600 text-2xl font-medium font-inter mb-2"
                htmlFor="id"
              >
                Benutzername<sup className="text-red">*</sup>
              </label>
              <input
                ref={idInputRef}
                className={`appearance-none border font-regular font-inter  border-gray rounded-lg w-full py-3 px-5 text-gray-600 placeholder:text-gray-400 focus:outline-none focus:shadow-outline ${
                  errors.id ? "border-red" : ""
                }`}
                id="id"
                type="text"
                placeholder="Enter ID"
                value={formData.id}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                maxLength={4}
              />
              {errors.id && (
                <p className="text-red text-sm mt-1">{errors.id}</p>
              )}
            </div>
            <div className="mb-5 font-inter">
              <label
                className="block text-gray-600 text-2xl font-medium font-inter mb-2"
                htmlFor="password"
              >
                4 Stellige Pin <sup className="text-red">*</sup>
              </label>
              <input
                className={`appearance-none border font-regular font-inter border-gray rounded-lg w-full py-3 px-5 text-gray-600 placeholder:text-gray-400 focus:outline-none focus:shadow-outline ${
                  errors.pin ? "border-red" : ""
                }`}
                id="pin"
                type="password"
                placeholder="Enter Pin"
                value={formData.pin}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                maxLength={4}
              />
              {errors.pin && (
                <p className="text-red text-sm mt-1">{errors.pin}</p>
              )}
            </div>
            <button
              className="bg-blue w-full uppercase hover:bg-blue-800 text-white font-roboto font-bold py-3 px-5 rounded-xl focus:outline-none focus:shadow-outline transition-all ease-in flex items-center justify-center"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              <CircularProgress
                size={20}
                color="inherit"
                className="mr-2"
                style={{ visibility: isLoading ? "visible" : "hidden" }} // Reserve space without changing width
              />
              Einloggen
            </button>

            <div className="text-center mt-5">
              <Link
                className="inline-block text-center font-bold font-inter text-2xl text-gray-700 transition-all ease-in hover:text-dark-gray"
                to="/forgot-password"
              >
                Pin Vergessen?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
